<template>
  <div>
    <dx-data-grid
      ref="usersGrid"
      :data-source="dataSource"
      :remote-operations="remoteOperations"
      :allow-column-reordering="true"
      :show-borders="true"
      :column-min-width="50"
      :column-auto-width="true"
      :allow-column-resizing="true"
      column-resizing-mode="widget"
      :hover-state-enabled="true"
      :on-row-dbl-click="onRowDblClick"
      :selected-row-keys.sync="selectedRowKeys"
      @toolbar-preparing="onToolbarPreparing"
      @editing-start="onEditingStart"
      @content-ready="onContentReady"
      @selection-changed="onSelectionChanged"
    >
      <dx-column
        data-field="id"
        caption="ID"
        :fixed="true"
        fixed-position="left"
        :allow-editing="false"
        :allow-header-filtering="false"
      />
      <dx-column
        v-if="isCompany"
        data-field="is_active"
        :caption="$t('Active')"
      />
      <dx-column
        v-if="isCompany && company.is_expedition_company"
        data-field="is_access_to_all_companies"
        :caption="$t('is_access_to_all_companies_short')"
      />
      <dx-column
        v-else
        data-field="state"
        :caption="$t('State')"
        :lookup="stateLookup"
        :fixed="true"
      />
      <dx-column
        data-field="username"
        :allow-editing="isCompany && !isEdit"
        :caption="$t('Username')"
      />
      <dx-column
        v-if="isCompany && company.is_expedition_company"
        data-field="roles"
        :allow-editing="false"
        :caption="$t('roles')"
        cell-template="roles-template"
      />
      <dx-column
        data-field="first_name"
        :caption="$t('First name')"
      />
      <dx-column
        data-field="last_name"
        :caption="$t('Last name')"
      />
      <dx-column
        data-field="email"
        :caption="$t('Email')"
      />
      <dx-column
        data-field="phone"
        :caption="$t('Phone')"
      />
      <template v-if="!isCompany">
        <dx-column
          data-field="company_id"
          :caption="$t('Company')"
          :lookup="companyLookup"
        />
        <dx-column
          data-field="request_inn"
          :caption="$t('INN')"
        />
        <dx-column
          data-field="request_name"
          :caption="$t('Organization name')"
        />
        <dx-column
          data-field="request_contract_nums"
          :caption="$t('Contract nums')"
        />
      </template>

      <template #roles-template="{ data }">
        {{ data.data.roles.map(it => $t(`company_page.roles.${it.name}`)).join(', ') }}
      </template>

      <template #newUserBtn="{}">
        <dx-button
          :text="$t('Add_employee')"
          type="success"
          styling-mode="contained"
          class="custom-btn mr-2"
          @click="showAddUserModal"
        />
      </template>

      <template #changeCompanyBtn="{}">
        <dx-button
          :text="$t('Change company')"
          :disabled="!selectedRowKeys.length"
          type="success"
          styling-mode="contained"
          class="custom-btn mr-2"
          @click="showChangeCompanyModal"
        />
      </template>

      <dx-group-panel :visible="true" />
      <dx-editing
        v-if="isCompany"
        :allow-updating="true"
        :allow-deleting="false"
        :allow-adding="false"
        :use-icons="true"
        mode="row"
      />
      <dx-header-filter :visible="true" />
      <dx-search-panel
        :visible="true"
        :highlight-case-sensitive="false"
      />
      <dx-export
        :enabled="true"
        file-name="freight-orders"
      />
      <dx-column-chooser :enabled="true" />
      <dx-selection
        show-check-boxes-mode="always"
        mode="single"
      />
      <dx-state-storing
        :enabled="true"
        type="localStorage"
        :storage-key="`u${user.id}-${localStorage}`"
      />
      <dx-paging :page-size="20" />
      <dx-pager
        :show-page-size-selector="true"
        :allowed-page-sizes="[10, 15, 20, 50]"
      />
    </dx-data-grid>
    <template v-if="selectedUser">
      <dx-popup
        :visible.sync="userModalShown"
        :drag-enabled="false"
        :show-title="true"
        :title="$t('employee')"
        :width="800"
      >
        <dx-scroll-view>
          <b-row>
            <b-col lg="12">
              <dx-form-group :label="$t('Username')">
                <dx-text-box
                  v-model="selectedUser.username"
                  :read-only="isEdit"
                  value-change-event="input"
                  styling-mode="outlined"
                  :is-valid="!errors.username"
                />
                <div
                  v-if="errors.username"
                  class="my-1 text-danger"
                >
                  {{ errors.username }}
                </div>
              </dx-form-group>
              <dx-form-group :label="$t('Password')">
                <dx-text-box
                  v-model="password"
                  :mode.sync="passwordMode"
                  value-change-event="input"
                  styling-mode="outlined"
                  :is-valid="!errors.password"
                >
                  <dx-text-box-button
                    location="after"
                    name="generatePassword"
                    :options="generatePasswordButton"
                  >
                    {{ $t('generate password') }}
                  </dx-text-box-button>
                  <dx-text-box-button
                    name="password"
                    :options="passwordButton"
                    location="after"
                  />
                </dx-text-box>
                <div
                  v-if="errors.password"
                  class="my-1 text-danger"
                >
                  {{ errors.password }}
                </div>
              </dx-form-group>
              <dx-form-group :label="$t('password confirmation')">
                <dx-text-box
                  v-model="passwordConfirm"
                  :mode.sync="passwordMode"
                  value-change-event="input"
                  styling-mode="outlined"
                  :is-valid="!errors.passwordConfirm"
                >
                  <dx-text-box-button
                    :options="passwordButton"
                    name="password"
                    location="after"
                  />
                </dx-text-box>
                <div
                  v-if="errors.passwordConfirm"
                  class="my-1 text-danger"
                >
                  {{ errors.passwordConfirm }}
                </div>
              </dx-form-group>
              <b-row>
                <b-col
                  v-for="it in userFields"
                  :key="it.label"
                  cols="12"
                  lg="6"
                >
                  <dx-form-group :label="it.label">
                    <dx-text-box
                      v-model="selectedUser[it.key]"
                      :read-only="it.readOnly"
                      value-change-event="input"
                      styling-mode="outlined"
                    />
                  </dx-form-group>
                </b-col>
              </b-row>
              <div class="d-flex flex-row align-items-start mb-4">
                <dx-check-box
                  v-model="selectedUser.is_active"
                  class="mr-2"
                />
                <div class="dx-field-label dx-field-label--fit">
                  {{ $t('Active') }}
                </div>
              </div>

              <div class="d-flex flex-row align-items-start mb-4">
                <dx-check-box
                  v-if="company && company.is_expedition_company"
                  v-model="selectedUser.is_access_to_all_companies"
                  class="mr-2"
                />
                <div class="dx-field-label dx-field-label--fit">
                  {{ $t('is_access_to_all_companies') }}
                </div>
              </div>

              <div
                v-if="!isEdit && company && company.is_expedition_company"
                class="d-flex flex-row mb-4"
              >
                <dx-check-box
                  v-model="isWarningChecked"
                  class="mr-2"
                />
                <div class="dx-field-label dx-field-label--fit text-danger">
                  {{ $t('change_company_warning') }}
                </div>
              </div>
            </b-col>
            <b-col>
              <dx-button
                :text="$t(!isEdit ? 'Add_employee' : 'Save')"
                type="success"
                styling-mode="contained"
                class="custom-btn mr-2 ml-auto"
                :disabled="loading || (!isEdit && company && company.is_expedition_company && !isWarningChecked)"
                @click="onSubmit"
              />
              <dx-button
                :text="$t('Cancel')"
                type="success"
                styling-mode="text"
                class="custom-btn"
                :disabled="loading"
                @click="userModalShown = false"
              />
            </b-col>
          </b-row>
        </dx-scroll-view>
      </dx-popup>
    </template>
    <template v-if="changeUserCompanyModalShown">
      <dx-popup
        :visible.sync="changeUserCompanyModalShown"
        :drag-enabled="false"
        :show-title="true"
        :title="$t('Change company')"
        :width="600"
        :height="350"
        @hidden="closeChangeUserCompanyModal"
      >
        <dx-scroll-view>
          <b-row>
            <b-col
              cols="12"
              class="mb-2"
            >
              <dx-form-group :label="$t('Company')">
                <dx-select-box
                  v-model="selectedForCompanyChangeUser.company_id"
                  :data-source="companies"
                  value-expr="id"
                  :display-expr="companyDisplayExpr"
                  styling-mode="filled"
                  @valueChanged="checkWarning"
                />
              </dx-form-group>
              <div
                v-if="isWarningCheckboxShown"
                class="d-flex flex-row mb-2"
              >
                <dx-check-box
                  v-model="isWarningChecked"
                  class="mr-2"
                />
                <div class="dx-field-label dx-field-label--fit text-danger">
                  {{ $t('change_company_warning') }}
                </div>
              </div>
            </b-col>
            <b-col cols="12">
              <dx-button
                :text="$t('Change company')"
                type="success"
                styling-mode="contained"
                class="custom-btn mr-2 ml-auto"
                :disabled="loading || (isWarningCheckboxShown && !isWarningChecked)"
                @click="changeCompany"
              />
              <dx-button
                :text="$t('Cancel')"
                type="success"
                styling-mode="text"
                class="custom-btn"
                :disabled="loading"
                @click="closeChangeUserCompanyModal"
              />
            </b-col>
          </b-row>
        </dx-scroll-view>
      </dx-popup>
    </template>
  </div>
</template>

<script>
import {
  DxDataGrid,
  DxExport,
  DxColumn,
  DxColumnChooser,
  DxEditing,
  DxHeaderFilter,
  DxGroupPanel,
  DxSearchPanel,
  DxSelection,
  DxStateStoring,
  DxPaging,
  DxPager,
} from 'devextreme-vue/data-grid';

import { DxButton, DxTextBox, DxSelectBox, DxCheckBox } from 'devextreme-vue'; //eslint-disable-line
import { DxScrollView } from 'devextreme-vue/scroll-view';
import { DxPopup } from 'devextreme-vue/popup';
import { DxButton as DxTextBoxButton } from 'devextreme-vue/text-box';

import { mapState } from 'vuex';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale as localize, loadMessages } from 'devextreme/localization';

import mixinDetictingMobile from '@/mixins/mixin-detecting-mobile';

import DxFormGroup from '@/components/common/DxFormGroup';

import createCustomStore from '@/utils/create-custom-store';
import createStateLookup from '@/utils/create-state-lookup';

const generator = require('generate-password');

export default {
  name: 'UsersGrid',
  components: {
    DxDataGrid,
    DxColumn,
    DxGroupPanel,
    DxSearchPanel,
    DxEditing,
    DxHeaderFilter,
    DxExport,
    DxColumnChooser,
    DxSelection,
    DxStateStoring,
    DxPaging,
    DxPager,
    DxButton,
    DxPopup,
    DxFormGroup,
    DxTextBox,
    DxCheckBox,
    DxSelectBox,
    DxScrollView,
    DxTextBoxButton,
  },
  mixins: [mixinDetictingMobile],
  props: {
    dataSource: Object,
    localStorage: String,
    companyId: [String, Number],
    company: Object,
    userSelectedId: [String, Number],
  },
  data: vm => ({
    isWarningCheckboxShown: false,
    isWarningChecked: false,
    userModalWasShown: false,
    remoteOperations: {
      sorting: true,
      filtering: true,
      paging: true,
    },
    stateLookup: createStateLookup(vm),
    userModalShown: false,
    selectedUser: null,
    selectedRowKeys: [],
    changeUserCompanyModalShown: false,
    selectedForCompanyChangeUser: null,
    loading: false,
    isEdit: false,
    password: '',
    passwordConfirm: '',
    errors: {
      username: null,
      password: null,
      passwordConfirm: null,
    },
    passwordMode: 'text',
    passwordButton: {
      icon: 'ace-icon icon-info',
      type: 'normal',
      onClick: () => {
        vm.passwordMode = vm.passwordMode === 'text' ? 'password' : 'text';
      },
    },
    generatePasswordButton: {
      text: vm.$t('generate password'),
      stylingMode: 'outline',
      type: 'default',
      elementAttr: {
        class: 'dx-button-success',
      },
      onClick: () => {
        vm.generatePassword();
      },
    },
  }),
  computed: {
    ...mapState(['user']),
    isCompany() {
      return !!this.companyId;
    },
    isProfileList() {
      return this.$route.name === 'UserList';
    },
    companyLookup: () => ({
      dataSource: {
        store: createCustomStore('profile/api/companies/'),
        key: 'id',
      },
      valueExpr: 'id',
      displayExpr(company) {
        return company ? `${company.name} ${company.inn || ''}` : '';
      },
      allowClearing: true,
    }),
    userFields() {
      return [
        {
          key: 'first_name',
          label: this.$t('First name'),
        },
        {
          key: 'last_name',
          label: this.$t('Last name'),
        },
        {
          key: 'phone',
          label: this.$t('Phone'),
        },
        {
          key: 'email',
          label: this.$t('Email'),
        },
      ];
    },
  },
  watch: {
    changeUserCompanyModalShown() {
      this.isWarningCheckboxShown = false;
      this.isWarningChecked = false;
    },
    userModalShown() {
      this.isWarningCheckboxShown = false;
      this.isWarningChecked = false;
    },
  },
  created() {
    loadMessages(ruMessages);
    localize(this.$i18n.locale);
    this.getCompanies();
  },
  methods: {
    onToolbarPreparing(e) {
      e.toolbarOptions.items.unshift({
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-refresh',
          onClick: this.refreshDataGrid.bind(this),
        },
      }, {
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-close',
          hint: this.$t('clear_filters'),
          onClick: this.clearFilter.bind(this),
        },
      });
      if (!this.isCompany) {
        e.toolbarOptions.items.unshift({
          location: 'after',
          widget: 'dxButton',
          template: 'changeCompanyBtn',
        });
      }
      if (!this.isMobile && this.isCompany) {
        e.toolbarOptions.items.unshift({
          location: 'after',
          widget: 'dxButton',
          template: 'newUserBtn',
        });
      }
    },
    onRowDblClick(e) {
      if (this.isProfileList) {
        if (!e.data.company_id) {
          return;
        }
        this.$router.push({
          name: 'Company',
          params: {
            id: e.data.company_id,
            formType: 'edit',
            userSelectedId: e.data.id,
          },
        });
        return;
      }

      this.userModalShown = true;
      this.isEdit = true;
      this.selectedUser = e.data;
      this.password = '';
      this.passwordConfirm = '';
      this.selectedUser._create_new_company = false; //eslint-disable-line
    },
    refreshDataGrid() {
      this.$refs.usersGrid.instance.refresh();
    },
    clearFilter() {
      this.$refs.usersGrid.instance.clearFilter();
    },
    getCompanies() {
      this.companies = {
        store: createCustomStore('profile/api/companies/'),
        paginate: true,
      };
    },
    companyDisplayExpr(company) {
      return company ? `${company.name} ${company.inn || ''}` : '';
    },
    async onSubmit() {
      this.loading = true;
      try {
        if (!this.isFormValid()) {
          return;
        }

        if (this.password) {
          this.selectedUser.password = this.password;
        }

        if (this.isEdit) {
          await this.$http.patch(`profile/api/users/${this.selectedUser.id}/`, this.selectedUser);
        } else {
          await this.$http.post(`profile/api/companies/${this.companyId}/users/`, this.selectedUser);
        }
        this.userModalShown = false;
        this.refreshDataGrid();
      } finally {
        this.loading = false;
      }
    },
    showAddUserModal() {
      this.isEdit = false;
      this.userModalShown = true;

      this.password = '';
      this.passwordConfirm = '';

      this.selectedUser = {
        username: '',
        first_name: '',
        last_name: '',
        company_id: '',
        phone: '',
        email: '',
        state: 'approved',
        password: '',
        is_active: true,
        is_access_to_all_companies: false,
      };
    },
    onSelectionChanged(e) {
      this.selectedForCompanyChangeUser = e.selectedRowsData[0];
    },
    showChangeCompanyModal() {
      this.changeUserCompanyModalShown = true;
    },
    async changeCompany() {
      this.loading = true;
      await this.$http
        .patch(`profile/api/users/${this.selectedForCompanyChangeUser.id}/`, this.selectedForCompanyChangeUser)
        .finally(() => {
          this.closeChangeUserCompanyModal();
          this.loading = false;
        });

      this.refreshDataGrid();
    },
    closeChangeUserCompanyModal() {
      this.selectedRowKeys = [];
      this.changeUserCompanyModalShown = false;
    },
    isFormValid() {
      this.errors.username = '';
      this.errors.password = '';
      this.errors.passwordConfirm = '';
      let hasErrors = false;

      if (!this.selectedUser.username) {
        this.errors.username = this.$t('Field is required');
        hasErrors = true;
      }

      if (!this.isEdit && !this.password) {
        this.errors.password = this.$t('Field is required');
        hasErrors = true;
      }

      if (this.password && this.password !== this.passwordConfirm) {
        this.errors.passwordConfirm = this.$t('Password and confirmation do not match');
        hasErrors = true;
      }

      return !hasErrors;
    },
    generatePassword() {
      this.password = generator.generate({
        length: 8,
      });

      this.passwordConfirm = this.password;
    },
    onEditingStart() {
      this.isEdit = true;
      this.password = '';
      this.passwordConfirm = '';
    },
    onContentReady() {
      this.$nextTick(() => {
        this.selectedRowKeys = [];
      });

      if (this.userSelectedId && !this.userModalWasShown) {
        const rowIndex = this.$refs.usersGrid.instance.getRowIndexByKey(this.userSelectedId);
        const [rowElem] = this.$refs.usersGrid.instance.getRowElement(rowIndex);
        rowElem.click();
        rowElem.click();

        this.userModalWasShown = true;
      }
    },
    checkWarning(company) {
      // eslint-disable-next-line
      this.isWarningCheckboxShown = company.component._dataSource._items.find(it => it.id === company.value).is_expedition_company;
    },
  },
};
</script>

<style lang="scss" scoped>
.dx-field-label {
  width: auto;
}
</style>
